"use client";

import Script from "next/script";
import { useEffect, useRef, useState } from "react";

type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  theme?: "light" | "dark" | "auto";
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
};

type TurnstileApi = {
  render: (container: Element, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

type PublicAntiBotConfigResponse = {
  enabled?: boolean;
  provider?: string;
  turnstileSiteKey?: string;
  isTestSiteKey?: boolean;
};

const TEST_SITE_KEY = "1x00000000000000000000AA";

type AntiBotRuntimeConfig = {
  enabled: boolean;
  provider: string;
  turnstileSiteKey: string;
  isTestSiteKey: boolean;
};

const fallbackConfig: AntiBotRuntimeConfig = {
  enabled:
    process.env.NEXT_PUBLIC_ANTI_BOT_ENABLED === "true" || process.env.ANTI_BOT_ENABLED === "true",
  provider: (process.env.ANTI_BOT_PROVIDER ?? "turnstile").trim().toLowerCase() || "turnstile",
  turnstileSiteKey: (process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "").trim(),
  isTestSiteKey: (process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "").trim() === TEST_SITE_KEY,
};

export default function HumanCheck({
  action,
  resetCounter = 0,
  onTokenChange,
  onRequirementChange,
  className = "",
}: {
  action: string;
  resetCounter?: number;
  onTokenChange: (token: string) => void;
  onRequirementChange?: (required: boolean) => void;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [config, setConfig] = useState<AntiBotRuntimeConfig>(fallbackConfig);

  useEffect(() => {
    let isCancelled = false;

    const resolveConfig = async () => {
      try {
        const response = await fetch("/api/anti-bot/config", {
          method: "GET",
          cache: "no-store",
        });
        if (!response.ok) return;
        const payload = (await response.json()) as PublicAntiBotConfigResponse;

        if (!isCancelled) {
          const runtimeKey = typeof payload.turnstileSiteKey === "string"
            ? payload.turnstileSiteKey.trim()
            : "";
          setConfig({
            enabled: payload.enabled === true,
            provider:
              typeof payload.provider === "string" && payload.provider.trim()
                ? payload.provider.trim().toLowerCase()
                : "turnstile",
            turnstileSiteKey: runtimeKey,
            isTestSiteKey: payload.isTestSiteKey === true || runtimeKey === TEST_SITE_KEY,
          });
        }
      } catch {
        // Keep fallback env config when runtime endpoint is temporarily unavailable.
      }
    };

    void resolveConfig();

    return () => {
      isCancelled = true;
    };
  }, []);

  const antiBotEnabled = config.enabled;
  const provider = config.provider;
  const turnstileSiteKey = config.turnstileSiteKey;
  const isTestSiteKey = config.isTestSiteKey;
  const captchaRequired =
    antiBotEnabled && provider === "turnstile" && Boolean(turnstileSiteKey);

  useEffect(() => {
    onRequirementChange?.(captchaRequired);
  }, [captchaRequired, onRequirementChange]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.turnstile) {
      setScriptLoaded(true);
      setScriptError(false);
    }
  }, []);

  useEffect(() => {
    if (!captchaRequired || scriptLoaded) {
      setScriptError(false);
      return;
    }
    if (typeof window === "undefined") return;

    const timeoutId = window.setTimeout(() => {
      if (!window.turnstile) {
        setScriptError(true);
      }
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [captchaRequired, scriptLoaded]);

  useEffect(() => {
    if (!antiBotEnabled) return;
    if (provider !== "turnstile") return;
    if (!turnstileSiteKey) return;
    if (!scriptLoaded) return;
    if (!containerRef.current) return;
    if (!window.turnstile) return;

    onTokenChange("");

    if (widgetIdRef.current) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: turnstileSiteKey,
      action,
      theme: "dark",
      callback: (token: string) => onTokenChange(token),
      "expired-callback": () => onTokenChange(""),
      "error-callback": () => onTokenChange(""),
    });

    return () => {
      if (!widgetIdRef.current || !window.turnstile) return;
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    };
  }, [action, antiBotEnabled, onTokenChange, provider, resetCounter, scriptLoaded, turnstileSiteKey]);

  if (!antiBotEnabled) return null;

  if (provider !== "turnstile") {
    return (
      <div className={className}>
        <p className="text-xs text-red-400">
          Proveedor anti-bot no soportado: {provider}.
        </p>
      </div>
    );
  }

  if (!turnstileSiteKey) {
    return (
      <div className={className}>
        <p className="text-xs text-red-400">
          Protección anti-bot no configurada (falta NEXT_PUBLIC_TURNSTILE_SITE_KEY).
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <Script
        id="turnstile-script"
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
        onLoad={() => {
          setScriptLoaded(true);
          setScriptError(false);
        }}
        onReady={() => {
          setScriptLoaded(true);
          setScriptError(false);
        }}
        onError={() => setScriptError(true)}
      />
      <div ref={containerRef} />
      {scriptError ? (
        <p className="text-[11px] text-red-400 mt-2">
          No pudimos cargar la verificación de Cloudflare. Recarga la página y desactiva bloqueadores para continuar.
        </p>
      ) : (
        <p className="text-[11px] text-gray-500 mt-2">
          Verificación anti-bot activa.
        </p>
      )}
      {isTestSiteKey ? (
        <p className="text-[11px] text-amber-400 mt-1">
          Turnstile está usando clave de prueba. Configura una clave real y reinicia/despliega.
        </p>
      ) : null}
    </div>
  );
}
